import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { formatVnd, formatDateTime } from '@/lib/format';

interface RecentTransactionsProps {
  department?: string;
  dateFrom?: string;
  dateTo?: string;
}

function parseDeptArg(department: string | undefined): number | 'unassigned' | undefined {
  if (!department) return undefined;
  if (department === 'unassigned') return 'unassigned';
  const n = Number(department);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function RecentTransactions({ department, dateFrom, dateTo }: RecentTransactionsProps) {
  const deptArg = parseDeptArg(department);
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-top', department ?? null, dateFrom ?? null, dateTo ?? null],
    queryFn: () =>
      api.listTransactions({
        page: 1,
        limit: 10,
        sort: 'amount_desc',
        excludeDeclines: true,
        ...(deptArg !== undefined ? { department: deptArg } : {}),
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      }),
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  const rows = data?.rows ?? [];

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-xs">Time</TableHead>
          <TableHead className="text-xs">Merchant</TableHead>
          <TableHead className="text-right text-xs">Amount</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell className="font-mono text-xs tabular-nums text-muted-foreground">
              {formatDateTime(r.occurred_at)}
            </TableCell>
            <TableCell className="max-w-[200px] truncate text-xs">
              <Link to={`/transactions/${r.id}`} className="hover:underline">
                {r.merchant_canonical ?? r.raw_merchant_text}
              </Link>
            </TableCell>
            <TableCell className="text-right font-mono text-xs tabular-nums">
              {formatVnd(r.amount_vnd)}
            </TableCell>
          </TableRow>
        ))}
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={3} className="py-6 text-center text-sm text-muted-foreground">
              No transactions yet
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
